/**
 * The proof of a pitch (owner decision 2026-10-27: the artist chooses). The artist picks at most 3
 * items: numbers from our data (`campaigns.proofOptions`) and one free line. The pitch writer (P4)
 * may use only these items as proof, and the song card shows them as "So far". Pure.
 */

/** The most proof items of one campaign. */
export const PITCH_PROOF_MAX_ITEMS = 3;

/** The most characters of the free line. */
export const PITCH_PROOF_FREE_LINE_MAX = 80;

/** The proof items that come from our data. */
export const PITCH_PROOF_DATA_KEYS = ['streams28d', 'monthlyListeners', 'followers', 'playlistAdds'] as const;
export type PitchProofDataKey = (typeof PITCH_PROOF_DATA_KEYS)[number];

/** Every proof key: the data keys and `custom`, the free line that the artist types. */
export const PITCH_PROOF_KEYS = [...PITCH_PROOF_DATA_KEYS, 'custom'] as const;
export type PitchProofKey = (typeof PITCH_PROOF_KEYS)[number];

/**
 * One proof item. For a data key, `value` is the short number ("18.4k") and `label` the words after
 * it ("streams in the last 28 days"). For `custom`, `value` is the line of the artist and `label` is
 * empty. The labels are English: the pitch writer translates them, and the web shows its own copy
 * by `key`.
 */
export interface PitchProofItem {
  key: PitchProofKey;
  label: string;
  value: string;
}

/** What `campaigns.update` takes: the keys, and the text of the free line. */
export interface PitchProofChoice {
  key: PitchProofKey;
  text?: string | null;
}

/** The English words after the number of each data key. */
export const PITCH_PROOF_LABELS: Record<PitchProofDataKey, string> = {
  streams28d: 'streams in the last 28 days',
  monthlyListeners: 'monthly listeners on Spotify',
  followers: 'followers on Spotify',
  playlistAdds: 'playlist adds so far',
};

export function isPitchProofKey(value: unknown): value is PitchProofKey {
  return typeof value === 'string' && (PITCH_PROOF_KEYS as readonly string[]).includes(value);
}

/** A number in short form: 950, 18.4k, 1.2M. */
export function formatProofNumber(value: number): string {
  const n = Math.max(0, Math.round(value));
  if (n < 1000) {
    return `${n}`;
  }
  if (n < 1_000_000) {
    const k = n / 1000;
    return `${k >= 100 ? Math.round(k) : Number(k.toFixed(1))}k`;
  }
  const m = n / 1_000_000;
  return `${m >= 100 ? Math.round(m) : Number(m.toFixed(1))}M`;
}

/** One item as one line: "18.4k streams in the last 28 days", or the free line as it is. */
export function pitchProofLine(item: Pick<PitchProofItem, 'label' | 'value'>): string {
  const label = item.label.trim();
  return label === '' ? item.value.trim() : `${item.value.trim()} ${label}`;
}

/** The free line as the API keeps it: one line, single spaces, at most 80 characters. */
export function cleanProofFreeLine(text: string): string {
  return text.replace(/\s+/g, ' ').trim().slice(0, PITCH_PROOF_FREE_LINE_MAX);
}

/**
 * The stored proof of a campaign (`OutreachCampaigns.proof`) as items. A broken row reads as no
 * proof; an unknown key or an empty value is left out; at most 3 items.
 */
export function readStoredProof(value: unknown): PitchProofItem[] {
  if (!Array.isArray(value)) {
    return [];
  }
  const items: PitchProofItem[] = [];
  for (const raw of value) {
    if (typeof raw !== 'object' || raw === null) {
      continue;
    }
    const { key, label, value: text } = raw as Record<string, unknown>;
    if (!isPitchProofKey(key) || typeof text !== 'string' || text.trim() === '') {
      continue;
    }
    items.push({ key, label: typeof label === 'string' ? label : '', value: text.trim() });
  }
  return items.slice(0, PITCH_PROOF_MAX_ITEMS);
}
